import React from 'react'
import { Calendar, Info } from 'lucide-react'

/**
 * DataFreshnessInfo - displays data freshness information for KAM Performance page.
 * Shows the last update date for each operator's per-salon sales data with descriptions.
 */

// Format a single operator's freshness for display
const formatOperatorFreshness = (operatorCode, freshnessData, thresholds) => {
  if (!freshnessData?.kam_performance) {
    return null
  }
  
  const { last_update, source_type, comment } = freshnessData.kam_performance
  const greenHours = thresholds?.green_hours || 24
  const yellowHours = thresholds?.yellow_hours || 48
  
  // Map operator codes to display names
  const operatorNames = {
    'ORANGE': 'Orange',
    'TMOBILE': 'T-Mobile',
    'PLUS': 'Plus',
    'P4': 'Play'
  }
  
  const displayName = operatorNames[operatorCode] || operatorCode
  
  // No data available
  if (!last_update && source_type !== 'live') {
    return {
      name: displayName,
      dateText: null,
      color: 'text-gray-400',
      bgColor: 'bg-gray-100',
      icon: '⚪',
      hasData: false,
      comment: comment || 'Brak danych per salon'
    }
  }
  
  // Live data
  if (source_type === 'live') {
    return {
      name: displayName,
      dateText: 'Na bieżąco',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      icon: '🟢',
      hasData: true,
      comment: comment || 'Dane aktualizowane na bieżąco'
    }
  }
  
  // Parse date
  const updateDate = new Date(last_update)
  const now = new Date()
  const hoursDiff = (now - updateDate) / (1000 * 60 * 60)
  
  // Format date as DD.MM.YYYY (full year)
  const dateText = updateDate.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  
  // Determine color based on freshness (using thresholds from rules.json)
  let color, bgColor, icon
  if (hoursDiff < greenHours) {
    color = 'text-green-600'
    bgColor = 'bg-green-50'
    icon = '🟢'
  } else if (hoursDiff < yellowHours) {
    color = 'text-yellow-600'
    bgColor = 'bg-yellow-50'
    icon = '🟡'
  } else {
    color = 'text-red-600'
    bgColor = 'bg-red-50'
    icon = '🔴'
  }
  
  return {
    name: displayName,
    dateText,
    color,
    bgColor,
    icon,
    hasData: true,
    comment: comment || 'Dane z raportu'
  }
}

const DataFreshnessInfo = ({ dataFreshness, thresholds }) => {
  if (!dataFreshness || Object.keys(dataFreshness).length === 0) {
    return null
  }
  
  // Order of operators to display
  const operatorOrder = ['ORANGE', 'PLUS', 'P4', 'TMOBILE']
  
  // Format freshness for each operator (passing thresholds)
  const operatorItems = operatorOrder
    .filter(code => dataFreshness[code])
    .map(code => formatOperatorFreshness(code, dataFreshness[code], thresholds))
    .filter(Boolean)
  
  if (operatorItems.length === 0) {
    return null
  }
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
      {/* Header with title */}
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-semibold text-blue-800">
          Aktualność danych sprzedażowych per salon
        </span>
        <div className="group relative">
          <Info className="h-3.5 w-3.5 text-blue-400 cursor-help" />
          <div className="absolute left-0 top-5 z-10 hidden group-hover:block w-72 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
            Poniżej przedstawiono daty ostatniej aktualizacji danych sprzedażowych 
            z podziałem na salony dla każdego operatora. Dane te są wykorzystywane 
            do obliczania realizacji celów KAM-ów.
          </div>
        </div>
      </div>
      
      {/* Operator items in grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {operatorItems.map((item) => (
          <div 
            key={item.name} 
            className={`rounded-md px-3 py-2 ${item.bgColor} border border-opacity-50 ${item.hasData ? 'border-current' : 'border-gray-300'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm">{item.icon}</span>
              <span className="text-sm font-semibold text-gray-800">{item.name}</span>
            </div>
            {item.dateText ? (
              <div className={`text-sm font-bold ${item.color}`}>
                {item.dateText}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                Brak danych
              </div>
            )}
            <div className="text-xs text-gray-600 mt-1 leading-tight">
              {item.comment}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default DataFreshnessInfo

